import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SimpleTable from "../../../components/tables/simpleTable";
import { useLoadingState } from "../../../components/loadingFeedback";
import { RadioButton } from "primereact/radiobutton";
import {
  callActionApiAsync,
  getCallInspectionProtocolData,
  postSaveInspectionProtocol
} from "../../../services/apiService";
import { YesNoNAOptions } from "../../../constants/selectData";

const InspectionProtocol = ({
  bioEquipmentId,
  isApprover,
  isViewer,
  isLocked,
  onProtocolValidationChange,
  onError,
  userId,
  startProtectedAction,
  endProtectedAction
}) => {
  const rowLockRef = useRef(false);
  const [activityTable, setActivityTable] = useState([]);
  const [loadingTable, setLoadingTable] = useLoadingState(true);
  const [savingRowId, setSavingRowId] = useState(null);
  const canEditStatus = !isViewer && !isApprover && !isLocked;

  const getInspectionProtocolData = useCallback(async () => {
    if (!bioEquipmentId) {
      setActivityTable([]);
      setLoadingTable(false);
      return;
    }

    await callActionApiAsync(
      () => getCallInspectionProtocolData(bioEquipmentId),
      (data) => {
        setActivityTable(data?.table || []);
      },
      (error) => {
        setActivityTable([]);
        onError?.(error, "Error al cargar el protocolo", "No se pudo cargar el protocolo de inspección. Recarga la página antes de guardar.");
      },
      setLoadingTable
    );
  }, [bioEquipmentId, setLoadingTable, onError]);

  const handleStatusChange = useCallback(async (rowData, optionValue) => {
    if (!canEditStatus || !rowData?.id || rowLockRef.current) return;
    if (startProtectedAction && !startProtectedAction("protocol")) return;
    rowLockRef.current = true;

    const previousRows = activityTable;
    setSavingRowId(rowData.id);
    setActivityTable((currentRows) =>
      currentRows.map((item) =>
        item.id === rowData.id ? { ...item, optionValue } : item
      )
    );

    try {
      await callActionApiAsync(
        () =>
          postSaveInspectionProtocol({
            requestId: crypto.randomUUID(),
            userId,
            id: rowData.id,
            activity: rowData.activityId,
            optionValue,
            bioEquipmentId
          }),
        async () => {
          await getInspectionProtocolData();
        },
        (error) => {
          setActivityTable(previousRows);
          onError?.(error, "Protocolo sin guardar", "No se pudo guardar el estado de la actividad. Intenta nuevamente.");
        }
      );
    } catch (error) {
      setActivityTable(previousRows);
      onError?.(error, "Protocolo sin guardar", "No se pudo guardar el estado de la actividad. Intenta nuevamente.");
    } finally {
      rowLockRef.current = false;
      setSavingRowId(null);
      endProtectedAction?.();
    }
  }, [activityTable, bioEquipmentId, canEditStatus, getInspectionProtocolData, userId, startProtectedAction, endProtectedAction, onError]);

  const inspectionProtocolColumns = useMemo(
    () => [
      { field: "activityName", header: "Actividades " },
      {
        field: "optionValue",
        header: "Estado",
        columnType: "custom",
        render: (rowData) => (
          <div className="IHContainer" style={{ gap: "12px", flexWrap: "wrap" }}>
            {YesNoNAOptions.map((option) => (
              <div key={`${rowData.id}-${option.value}`} className="IHContainer align-center">
                <RadioButton
                  inputId={`protocol-${rowData.id}-${option.value}`}
                  name={`protocol-${rowData.id}`}
                  value={option.value}
                  onChange={(e) => handleStatusChange(rowData, e.value)}
                  checked={Number(rowData.optionValue) === option.value}
                  disabled={rowData.isRowDisabled}
                />
                <label
                  htmlFor={`protocol-${rowData.id}-${option.value}`}
                  style={{ marginLeft: 6 }}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )
      }
    ],
    [handleStatusChange]
  );

  // DataTable omite re-renderizar filas con rowData igual; el estado disabled debe viajar en los datos.
  const tableData = useMemo(
    () =>
      activityTable.map((item) => ({
        ...item,
        isRowDisabled: !canEditStatus || savingRowId !== null
      })),
    [activityTable, canEditStatus, savingRowId]
  );

  useEffect(() => {
    getInspectionProtocolData();
  }, [getInspectionProtocolData]);

  useEffect(() => {
    if (!onProtocolValidationChange) return;

    const hasRows = activityTable.length > 0;
    const isComplete =
      !loadingTable && hasRows &&
      activityTable.every(
        (item) => item.optionValue != null && item.optionValue !== ""
      );

    onProtocolValidationChange({
      isLoading: loadingTable,
      hasRows,
      isComplete,
      rows: activityTable
    });
  }, [activityTable, loadingTable, onProtocolValidationChange]);

  return (
    <div className="page-break">
      <p className="createRist-title mt-25 ml-0">2. Protocolo de Inspección</p>
    

      <SimpleTable
        loading={loadingTable}
        data={tableData}
        columns={inspectionProtocolColumns}
        emptyMessage="No hay actividades definidas para este equipo o su modelo."
      />
    </div>
  );
};

export default InspectionProtocol;
